/**
 * Perfis de Usuário - Fonte Única de Verdade
 *
 * Sistema padronizado de perfis de acesso com 5 níveis.
 * Sincronizado com backend (inventory/models/user.py)
 *
 * @see backend/django_project/inventory/models/user.py - UserProfile.ROLE_CHOICES
 * @see backend/django_project/inventory/models/perfil.py - Epic 27 Perfis
 */

/**
 * Constantes de perfis (valores do banco de dados)
 */
export const ROLES = {
  MASTER: 'MASTER',
  AUDITOR: 'AUDITOR',
  ANALISTA: 'ANALISTA',
  CLIENTE: 'CLIENTE',
  CONFERENTE: 'CONFERENTE',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/**
 * Labels amigáveis para exibição
 */
export const ROLE_LABELS: Record<Role, string> = {
  [ROLES.MASTER]: 'Master',
  [ROLES.AUDITOR]: 'Auditor',
  [ROLES.ANALISTA]: 'Analista',
  [ROLES.CLIENTE]: 'Cliente',
  [ROLES.CONFERENTE]: 'Conferente',
};

/**
 * Cores padronizadas para badges e displays
 * Formato Tailwind: bg-{color}-100, text-{color}-700, border-{color}-300
 */
export const ROLE_COLORS: Record<Role, { bg: string; text: string; border: string; darkBg: string; darkText: string; darkBorder: string }> = {
  [ROLES.MASTER]: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-300',
    darkBg: 'dark:bg-purple-900/30',
    darkText: 'dark:text-purple-400',
    darkBorder: 'dark:border-purple-700',
  },
  [ROLES.AUDITOR]: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300',
    darkBg: 'dark:bg-green-900/30',
    darkText: 'dark:text-green-400',
    darkBorder: 'dark:border-green-700',
  },
  [ROLES.ANALISTA]: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    border: 'border-cyan-300',
    darkBg: 'dark:bg-cyan-900/30',
    darkText: 'dark:text-cyan-400',
    darkBorder: 'dark:border-cyan-700',
  },
  [ROLES.CLIENTE]: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-300',
    darkBg: 'dark:bg-orange-900/30',
    darkText: 'dark:text-orange-400',
    darkBorder: 'dark:border-orange-700',
  },
  [ROLES.CONFERENTE]: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300',
    darkBg: 'dark:bg-yellow-900/30',
    darkText: 'dark:text-yellow-400',
    darkBorder: 'dark:border-yellow-700',
  },
};

/**
 * Níveis de acesso numéricos (apenas para controle interno)
 * NÃO devem ser exibidos na interface do usuário
 *
 * Usado para: Validações de permissão, hierarquia de acesso, regras de negócio
 */
export const ROLE_LEVELS: Record<Role, number> = {
  [ROLES.MASTER]: 10,      // N10 - Acesso Total
  [ROLES.AUDITOR]: 8,      // N8  - Realiza Contagens
  [ROLES.ANALISTA]: 7,     // N7  - Análise e Cadastros
  [ROLES.CLIENTE]: 5,      // N5  - Visualiza Próprios Dados
  [ROLES.CONFERENTE]: 3,   // N3  - Apenas Relatórios
};

/**
 * Descrições completas dos perfis (para tooltips, ajuda, etc.)
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  [ROLES.MASTER]: 'Acesso total ao sistema - Administração completa de usuários, hospitais, clientes e configurações',
  [ROLES.AUDITOR]: 'Realiza contagens em qualquer hospital - Cria e gerencia contagens de estoque',
  [ROLES.ANALISTA]: 'Análise e cadastros - Visualiza contagens, gera relatórios e gerencia cadastros',
  [ROLES.CLIENTE]: 'Visualiza próprios dados - Acesso limitado aos próprios hospitais e relatórios',
  [ROLES.CONFERENTE]: 'Apenas relatórios - Visualização de relatórios e conferências de dados',
};

/**
 * Array de perfis para iteração (ex: select options)
 */
export const ROLES_LIST: Role[] = Object.values(ROLES);

/**
 * Options para componentes de select/dropdown
 */
export const ROLE_OPTIONS = ROLES_LIST.map(role => ({
  value: role,
  label: ROLE_LABELS[role],
}));

/**
 * Verifica se um perfil é válido
 */
export const isValidRole = (role: string): role is Role => {
  return ROLES_LIST.includes(role as Role);
};

/**
 * Verifica se um usuário tem nível de acesso mínimo
 * @param userRole Perfil do usuário
 * @param minimumRole Perfil mínimo requerido
 * @returns true se usuário tem nível >= mínimo
 */
export const hasMinimumAccessLevel = (userRole: Role, minimumRole: Role): boolean => {
  return ROLE_LEVELS[userRole] >= ROLE_LEVELS[minimumRole];
};

/**
 * Verifica se usuário é Master (acesso total)
 */
export const isMasterRole = (role: string): boolean => {
  return role === ROLES.MASTER;
};
